import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, TextField, FormControl, IconButton, InputAdornment, FormControlLabel, Checkbox, CardMedia, Slider, FormHelperText } from "@mui/material";
import { Category } from "../../../../types/models/Category";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import HorizontalMenu from "../../components/creatingBeacon/HorizontalMenu";
import { FormikErrors, FormikTouched } from "formik";
import { Settings } from "../../../../types/models/Settings";
import { AppColor, AppConstants } from "../../../../types/Constants";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import { getImageDimensions, getImageURLCategoryListIcon, resizeImage } from "../../../../utils/utils";
import { useAppSelector } from "../../../../utils/hooks";
import ColorPicker from "../../../../components/ColorPicker";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UploadImageModal from "../../../../components/UploadImageModal";
import { Point } from "react-easy-crop";

type CategoriesMenuProps = {
    category: Category | null,
    values: Settings,
    errors: FormikErrors<Settings>,
    touched: FormikTouched<Settings>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Settings>>,
    setOpenFileSizeExceededMessage: React.Dispatch<React.SetStateAction<boolean>>;
    experienceMode: string
};

const CategoriesMenu = ({ category, values, errors, touched, setFieldValue, setOpenFileSizeExceededMessage, experienceMode}: CategoriesMenuProps) => {

    const businessGroupState = useAppSelector((state) => state.businessGroup);

    const [currentFilterFile, setCurrentFilterFile] = useState<File | undefined>(undefined);
    const [filterError, setFilterError] = useState<string>("");
    const [imageFilterURL, setImageFilterURL] = useState<string>("");

    const [selectedCategory, setSelectedCategory] = useState<Category | null>(category);

    const [openDeleteGroupMessage, setOpenDeleteGroupMessage] = useState<boolean>(false);

    const [openImageUpload, setOpenImageUpload] = useState<boolean>(false);
    const oldFilterFileRef = useRef<File | undefined>();
    const oldImageFilterURLRef = useRef<string>("");

    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const nameLimit = 20;

    useEffect(() => {
        setImageFilterURL("");
        setCurrentFilterFile(undefined);
        setSelectedCategory(category);
        
        let objectUrl = "";

        if (category?.currentFilterFile) {
            objectUrl = URL.createObjectURL(category.currentFilterFile);
            setImageFilterURL(objectUrl);
        } else {
            setImageFilterURL(getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? ""));
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };

    }, [category]);

    useEffect(() => {

        // Reset category selection when group changes
        setSelectedCategory(null);

    }, [businessGroupState.currentBusinessGroup]);
    

    const clearFileField = (setState: React.Dispatch<React.SetStateAction<File | undefined>>, setUrl: React.Dispatch<React.SetStateAction<string>>) => {
        setState(undefined);
        setUrl(getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? ""));
        updateSelectedCategoryField("currentFilterFile", null);
    };

     const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = event.target.files![0];

        getImageDimensions(inputFile, (width, height) => {
          if (width == 256 && height == 256) {
            setImageFilterURL(URL.createObjectURL(inputFile));
            setCurrentFilterFile(inputFile);
            updateSelectedCategoryField("currentFilterFile", inputFile);
          } else {
            resizeImage(inputFile, 256, 256, AppConstants.MaxImageBytes, (resizedFile?: File) => {
              if (resizedFile) {
                setImageFilterURL(URL.createObjectURL(resizedFile));
                setCurrentFilterFile(resizedFile);
                updateSelectedCategoryField("currentFilterFile", resizedFile);
              } else {
                setOpenFileSizeExceededMessage(true);
              }
            });
          }
        });

        oldFilterFileRef.current = currentFilterFile;
        oldImageFilterURLRef.current = imageFilterURL;

        setOpenImageUpload(true);
        resetCropAndZoom();
        
        event.target.value = "";
      };
    
    const isDuplicateName = () => {
        if (selectedCategory) {
            const trimmedName = selectedCategory.Name.trim().toLowerCase();
            
            if (trimmedName === "") return false;

            const matched = values.categories?.some(x => x.Name.trim().toLowerCase() === trimmedName && x.Id !== selectedCategory.Id) ?? false;
            return matched;
        } else {
            return false;
        }
    };

    const handleDeleteCategory = () => {
        if (!selectedCategory) return;
    
        // Remove the selected category from the categories array
        const updatedCategories = values.categories?.filter(cat => cat.Id !== selectedCategory.Id);
    
        // Update the form state with the new categories array
        setFieldValue("categories", updatedCategories);
        
        // Clear image and selected category
        setImageFilterURL("");
        setCurrentFilterFile(undefined);
        setSelectedCategory(null);

        setOpenDeleteGroupMessage(false);
    };

    const updateSelectedCategoryField = (field: keyof Category, value: any) => {
        if (!selectedCategory) return;

    
        const updatedCategory = { ...selectedCategory, [field]: value };
        setSelectedCategory(updatedCategory);
    
        const updatedCategories = values.categories?.map(cat =>
            cat.Id === updatedCategory.Id ? updatedCategory : cat
        );
    
        setFieldValue("categories", updatedCategories);
    };

    const resetCropAndZoom = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    if (selectedCategory) {
        return (
            <>
            <Box sx={{ maxWidth:"700px", flexGrow: 1, height: "auto" }}>
                <FormControl fullWidth sx={{ paddingY: "16px" }}>
                <Typography mb={4} variant="body1" fontWeight={600} fontSize={18}>Category Name</Typography>
                    <TextField
                        id="category" 
                        value={selectedCategory?.Name || ""}
                        onChange={(e) => {
                            updateSelectedCategoryField("Name", e.target.value);
                        }}
                        disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}
                        variant="outlined" 
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        autoComplete="off"
                        spellCheck="false"
                        aria-autocomplete="none"
                        autoCorrect="off"
                        error={isDuplicateName() || (touched.categories && Boolean(errors.categories) && selectedCategory.Name.length < 1)}
                        helperText={
                            isDuplicateName()
                                ? "Category name already in use."
                                : touched.categories && errors.categories && selectedCategory.Name.length < 1
                                ? errors.categories
                                : `${(selectedCategory?.Name.length ?? 0)}/${nameLimit}`
                        }
                        slotProps={{
                            formHelperText: {
                            sx: {
                                textAlign: "right",
                            },
                            },
                        }}
                        sx={{ paddingX: 2 }}
                        inputProps={{ maxLength: nameLimit }}
                    />
                </FormControl>
                <Box sx={{ paddingY: '16px' }}>
                <Box display="flex" gap={35}>
                    <FormControl>
                    <Box mt={0} display="flex" flexDirection="column" alignItems="left">
                    <Typography variant="body1" textAlign="center" fontWeight={600} fontSize={18}>Icon</Typography>
                    <Box 
                        sx={{ 
                            height: "110px", 
                            display: "flex",
                            justifyContent: "center",
                        }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "80px",
                            height: "80px",
                            border: "1px black solid",
                            mt: 6,
                            backgroundColor: selectedCategory?.Color && !selectedCategory.Color.startsWith("#") 
                                            ? `#${selectedCategory.Color}` 
                                            : selectedCategory?.Color || "#D0CCCC",
                            overflow: "hidden"
                        }}>
                        {imageFilterURL ? (
                            <CardMedia
                                sx={{ width: "100%", height: "100%", objectFit: "fill", overflow: "hidden" }}
                                component="img"
                                src={imageFilterURL}
                                alt="beacon"
                            />
                        ) : (
                            <>
                                <ImageOutlinedIcon
                                    style={{
                                        fontSize: 40,
                                        color: 'silver',
                                    }}
                                />
                            </>
                        )}
                    </Box>
                    </Box>
                        <Box sx={{ display: "flex", alignItems: "flex-start", mt: 10, width: "320px", paddingX: 2 }}>
                        <TextField
                            id="upload-filter"
                            value={currentFilterFile ? currentFilterFile.name : category?.currentFilterFile?.name ?? ""}
                            size="small"
                            variant="outlined"
                            helperText={filterError || null}
                            error={!!filterError}
                            inputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "240px", height: "40px" }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                        sx={{ 
                                            padding: 1, 
                                            fontSize: "16px", 
                                            visibility: currentFilterFile ? "visible" : "hidden" }} 
                                            onClick={() => clearFileField(setCurrentFilterFile, setImageFilterURL)}>
                                                <ClearIcon sx={{ fontSize: "20px"}} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <label htmlFor="file-input-filter" style={{ marginLeft: '8px' }}>
                            <Button variant="outlined" component="span" sx={{ height: "40px" }} disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}>
                                Browse
                            </Button>
                        </label>
                        <input
                            id="file-input-filter"
                            type="file"
                            accept=".png,.jpg,.jpeg,.svg"
                            style={{ display: 'none' }}
                            onChange={handleFilterChange}
                            disabled={experienceMode == "READ_ONLY"}
                        />
                        </Box>
                    </Box>
                    </FormControl>
                    <FormControl>
                    <Box mt={0} display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Typography variant="body1" textAlign="center" fontWeight={600} fontSize={18}>Color</Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "110px" }}>
                            <Box sx={{ 
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "80px",
                                height: "80px",
                                mt: 6,
                            }}>
                                <ColorPicker 
                                    color={selectedCategory?.Color || "#D0CCCC"}
                                    handleColorChange={(e) => updateSelectedCategoryField("Color", e)}
                                    experienceMode={experienceMode}
                                />
                            </Box> 
                        </Box>
                        </Box>
                    </FormControl>
                </Box>
                </Box>
                <FormControl fullWidth sx={{ paddingY: "16px" }}>
                <Typography variant="body1" fontWeight={600} fontSize={18} mb={4}>Beacon Type</Typography>
                <Box sx={{ display: "flex", gap: 10, paddingX: 2 }}>
                    <HorizontalMenu 
                        onModelSelect={(e) => updateSelectedCategoryField("Theme", e)}
                        model={selectedCategory?.Theme}
                        experienceMode={experienceMode}
                    />
                    <FormControlLabel 
                        control={
                            <Checkbox 
                                checked={selectedCategory?.ShowDistance ?? true} 
                                onChange={(e) => updateSelectedCategoryField("ShowDistance", e.target.checked)}
                            />
                        } 
                        label="Include Distance" 
                        sx={{ whiteSpace: "nowrap" }} 
                    />
                </Box>
                </FormControl>
                <FormControl fullWidth sx={{ paddingY: "16px" }}>
                <Typography variant="body1" fontWeight={600} fontSize={18} mb={4}>Beacon Height</Typography>
                    <Box sx={{ paddingX: 6, width: "50%" }}>
                        <Slider
                            value={
                                selectedCategory?.BeaconHeight == "High" ? 2 : selectedCategory?.BeaconHeight == "Medium" ? 1 : 0
                            }
                            onChange={(e, newValue) => {
                                const heights = ["Low", "Medium", "High"];
                                updateSelectedCategoryField("BeaconHeight", heights[newValue as number]);
                            }}
                            step={1}
                            marks={[
                                {value: 0, label: "Low"},
                                {value: 1, label: "Medium"},
                                {value: 2, label: "High"},
                            ]}
                            min={0}
                            max={2}
                            disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}
                        />
                    </Box>
                    <FormHelperText sx={{ paddingX: 2 }}>
                        Beacon Height controls the height above the ground beacons show up. For best experience, select Low for landscapes and High for cityscapes.
                    </FormHelperText>
                </FormControl>
                <FormControl sx={{ paddingY: "16px", marginBottom: "16px" }}>
                    <Typography mb={4} mt={-2} variant="body1" fontWeight={600} fontSize={18}>Visible Distance</Typography>
                    <Box display="flex" flexDirection="row" gap={8} paddingX={2}>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Box display="flex" gap={2} mt={2}>
                            <TextField
                                id="mindistance"
                                sx={{ width: 300 }}
                                value={selectedCategory?.MinDistanceVisible ?? ""}
                                disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}
                                label="Min Distance"
                                placeholder="0"
                                size="small"
                                variant="outlined"
                                onChange={(e) => {
                                    const re = /^[0-9\b]+$/;
                                    const value = e.target.value;
                                    if (value === '' || re.test(value)) {
                                        updateSelectedCategoryField("MinDistanceVisible", value === "" ? null : value);
                                    }
                                }}
                                helperText={"Beacons in this category won’t show if they are closer than this distance."}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton 
                                            sx={{ 
                                                padding: 1, 
                                                fontSize: "16px", 
                                                visibility: selectedCategory?.MinDistanceVisible ? "visible" : "hidden"
                                            }}
                                                onClick={() => updateSelectedCategoryField("MinDistanceVisible", null)}
                                            > 
                                                    <ClearIcon sx={{ fontSize: "20px"}} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Typography variant="body1" color="#888888" fontSize={14} mt={5}>meters</Typography>
                        </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="left">
                        <Box display="flex" gap={2} mt={2}>
                            <TextField
                                id="maxdistance"
                                value={selectedCategory?.MaxDistanceVisible ?? ""}
                                disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}
                                label="Max Distance"
                                placeholder="2000"
                                size="small"
                                variant="outlined"
                                onChange={(e) => {
                                    const re = /^[0-9\b]+$/;
                                    const value = e.target.value;
                                    if (value === '' || re.test(value)) {
                                        const limitValue = value === "" ? null : Math.min(Number(value), 2000);
                                        updateSelectedCategoryField("MaxDistanceVisible", limitValue === null ? null : limitValue.toString());
                                    }
                                }}
                                helperText={"Beacons in this category won’t show if they are further than this distance. Max 2000m."}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 300 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton sx=
                                            {{ 
                                                padding: 1, 
                                                fontSize: "16px",
                                                visibility: selectedCategory?.MaxDistanceVisible ? "visible" : "hidden", 
                                            }}
                                                onClick={() => updateSelectedCategoryField("MaxDistanceVisible", null)} 
                                            >
                                                <ClearIcon  sx={{ fontSize: "20px"}}/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="body1" color="#888888" fontSize={14} mt={5}>meters</Typography>
                        </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", mt: 8 }}>
                        <Button onClick={() => setOpenDeleteGroupMessage(true)} sx={{ color: "#FC7535", backgroundColor: AppColor.orangeBackground, padding: "10px 20px" }} disabled={experienceMode === "MOD_ONLY" || experienceMode === "READ_ONLY"}>Delete</Button>
                    </Box>
                </FormControl>
                <AppConfirmDialog
                    open={openDeleteGroupMessage}
                    onConfirm={handleDeleteCategory}
                    onDeny={() => {
                    setOpenDeleteGroupMessage(false);
                    }}
                    title="Are you sure you want to delete category: "
                    content={category?.Name + "?"}
                    dialogTitle="Delete Category"
                />
            </Box>
            <UploadImageModal 
                open={openImageUpload}
                onConfirm={() => setOpenImageUpload(false)}
                onDeny={() => {
                    setOpenImageUpload(false);
                    if (imageFilterURL && imageFilterURL !== oldImageFilterURLRef.current) {
                        URL.revokeObjectURL(imageFilterURL);
                    }

                    setCurrentFilterFile(oldFilterFileRef.current);
                    setImageFilterURL(oldImageFilterURLRef.current);
                    updateSelectedCategoryField("currentFilterFile", oldFilterFileRef.current);
                    resetCropAndZoom();
                }}
                confirmButtonTitle={"Save"}
                denyButtonTitle={"Cancel"}
                setCurrentAssetFile={(croppedFile) => {
                    if(croppedFile){
                        setCurrentFilterFile(croppedFile);
                        updateSelectedCategoryField("currentFilterFile", croppedFile);
                    }
                }}
                setCurrentImageURL={setImageFilterURL}
                imageFile={currentFilterFile}
                imageURL={imageFilterURL}
                paddingValue={0.05}
                crop={crop}
                setCrop={setCrop}
                zoom={zoom}
                setZoom={setZoom}
            />
            </>
        );
    } else {
        return (
            <Box sx={{ minWidth:"700px", height: "auto" }}>
                    <Box 
                    sx={{ 
                    height: "700px", 
                    maxWidth: "700px", 
                    backgroundColor: "white", 
                    padding: '16px', 
                    marginTop: '16px',  
                    }}> 
                    <Typography display="flex" justifyContent="center" alignItems="center" marginTop="20%" variant="body1" color="#cbcbcb"> Select a Category</Typography> 
                </Box>
            </Box>
        );
    }
};

export default CategoriesMenu;
